<template>
    <div class="card card-body shadow-sm mt-0">
      <div class="form-header d-flex justify-content-between align-items-center">
        <h4 class="form-title">{{ subconta.id ? "Editar Subconta" : "Nova Subconta" }}</h4>
        <button class="btn btn-secondary btn-sm me-3" @click="voltar">Voltar</button>
      </div>
      <BaseForm
        :submitText="subconta.id ? 'Salvar' : 'Salvar'"
        :cancelText="'Cancelar'"
        :isFormValid="isFormValid"
        :isSubmitting="isSubmitting"
        @submit="salvarSubconta"
        @cancel="voltar"
      >
        <!-- Informações da Subconta -->
        <FormSection title="Informações da Subconta">
          <div class="d-flex justify-content-between">
            <div class="form-group flex-grow-1 me-3">
              <label for="nome">Nome <span class="required">*</span></label>
              <input
                id="nome"
                type="text"
                v-model="subconta.nome"
                class="form-control"
                :class="{ 'error-input': errors.nome }"
                placeholder="Digite o nome da subconta"
              />
              <span v-if="errors.nome" class="error-message">{{ errors.nome }}</span>
            </div>
  
            <div class="form-group flex-grow-1 me-3">
                <label for="plano">Plano de Contas <span class="required">*</span></label>
                <select
                    id="plano"
                    v-model="subconta.planos_de_contas_id"
                    class="form-select"
                >
                    <option disabled value="">Selecione um plano de contas</option>
                    <option v-for="plano in planos" :key="plano.id" :value="plano.id">
                    {{ plano.nome }}
                    </option>
                </select>
                <span v-if="errors.planos_de_contas_id" class="error-message">{{ errors.planos_de_contas_id }}</span>
            </div>
  
            <div class="form-group">
              <label for="status">Status <span class="required">*</span></label>
              <div class="form-switch">
                <input
                  type="checkbox"
                  id="status"
                  v-model="subconta.ativo"
                  class="form-check-input"
                />
                <label for="status" class="form-check-label">
                  {{ subconta.ativo ? "Ativo" : "Inativo" }}
                </label>
              </div>
            </div>
          </div>
        </FormSection>
  
        <FormSection title="">
          <div class="form-group mt-3 w-100">
            <label for="descricao">Descrição</label>
            <textarea
              id="descricao"
              v-model="subconta.descricao"
              class="form-control"
              placeholder="Digite a descrição da subconta"
              rows="3"
              style="width: 100%;"
            ></textarea>
          </div>
        </FormSection>
      </BaseForm>
    </div>
  </template>
  
  <script>
  import { reactive, ref, computed, onMounted } from "vue";
  import { useRouter, useRoute } from "vue-router";
  import axios from "@/services/axios";
  import BaseForm from "@/components/Layout/BaseForm.vue";
  import FormSection from "@/components/Layout/FormSection.vue";
  
  export default {
    components: { BaseForm, FormSection },
    setup() {
      const router = useRouter();
      const route = useRoute();
  
      const subconta = reactive({
        id: null,
        nome: "",
        planos_de_contas_id: "",
        descricao: "",
        ativo: true,
      });
  
      const planos = ref([]);
      const errors = reactive({});
      const isSubmitting = ref(false);
  
      const carregarSubconta = async () => {
            const id = route.params.id;
            if (!id) return;

            try {
                const response = await axios.get(`/financeiro/subcontas/${id}`);
                const data = response.data;

                // Atribuir os dados da subconta ao objeto reativo
                subconta.id = data.id;
                subconta.nome = data.nome;
                subconta.planos_de_contas_id = data.planos_de_contas_id || ""; // Certifica-se de que há um valor, mesmo vazio
                subconta.descricao = data.descricao;
                subconta.ativo = data.ativo;

                // Verificar se o plano de contas atual está na lista carregada
                if (!planos.value.find(plano => plano.id === subconta.planos_de_contas_id)) {
                await carregarPlanos(); // Recarrega os planos de contas caso necessário
                }
            } catch (error) {
                console.error("Erro ao carregar subconta:", error);
            }
        };
  
        const carregarPlanos = async () => {
          try {
            const { data } = await axios.get('/financeiro/planos-contas', {
              params: {
                limit: 'all',     // sem paginação
                sort: 'nome',     // ordena por nome no backend
                order: 'asc',
                // ativo: 'true', // descomente se quiser só ativos
              }
            });

            const rows = Array.isArray(data?.data) ? data.data : [];

            // garante ordenação estável no front (case/acento-insensitive)
            const collator = new Intl.Collator('pt-BR', { sensitivity: 'base', numeric: true });
            rows.sort((a, b) => collator.compare(a?.nome || '', b?.nome || ''));

            // 🔸Escolha UMA das duas linhas abaixo, conforme seu <SelectField>:

            // 1) Se o Select usa o array “cru” (com {id, nome,...}):
            planos.value = rows;

            // 2) Se o Select espera opções { value, text }:
            // planos.value = rows.map(p => ({ value: p.id, text: p.nome }));

          } catch (err) {
            console.error('Erro ao carregar Planos de Contas:', err?.response?.data || err);
            planos.value = [];
          }
        };
  
      const validateForm = () => {
        errors.nome = subconta.nome ? "" : "O nome é obrigatório.";
        errors.planos_de_contas_id = subconta.planos_de_contas_id ? "" : "O plano de contas é obrigatório.";
      };
  
      const isFormValid = computed(() => {
        return Boolean(
          !Object.values(errors).some((error) => error) &&
          subconta.nome &&
          subconta.planos_de_contas_id
        );
      });
  
      const salvarSubconta = async () => {
        if (isSubmitting.value) return;
  
        validateForm();
  
        if (!isFormValid.value) {
          console.error("O formulário contém erros:", errors);
          return;
        }
  
        isSubmitting.value = true;
        try {
          const method = subconta.id ? "put" : "post";
          const url = subconta.id
            ? `/financeiro/subcontas/${subconta.id}`
            : "/financeiro/subcontas";
  
          await axios[method](url, subconta);
  
          router.push("/financeiro/subcontas");
        } catch (error) {
          console.error("Erro ao salvar subconta:", error);
        } finally {
          isSubmitting.value = false;
        }
      };
  
      const voltar = () => {
        router.push("/financeiro/subcontas");
      };
  
      onMounted(async () => {
        await carregarPlanos(); // Carrega os planos de contas primeiro
        await carregarSubconta(); // Depois carrega os detalhes da subconta
    }); 
  
      return {
        subconta,
        planos,
        errors,
        isFormValid,
        isSubmitting,
        salvarSubconta,
        voltar,
      };
    },
  };
  </script>
  
  <style scoped>
  .form-container {
    max-width: 90%;
    margin: 0 auto;
    padding: 20px;
    border: 1px solid #ddd;
    border-radius: 8px;
    background-color: #fff;
  }
  
 
  .form-group {
    margin-bottom: 15px;
  }
  
  .required {
    color: red;
  }
  
  .form-switch {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  
  </style>
  